// src/components/ErrorBoundary.tsx
// Klassen-basierte React ErrorBoundary – fängt Fehler in Lazy-Komponenten ab
// und zeigt eine thematisch passende Fehlermeldung statt eines leeren Bildschirms
//
// Unter »Technische Details« stehen zusätzlich die letzten Meldungen aus
// `src/lib/protokoll.ts`. Der Grund ist der Spieltisch: Dort hält niemand eine
// Browserkonsole in der Hand. Stürzt das Kampfblatt auf einem Handy ab, war
// die Antwort auf »was stand da?« bisher immer »weiß ich nicht«. Jetzt steht
// der Weg dorthin auf dem Bildschirm, und zwar der jüngste Schritt zuerst.

import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { protokoll, eintraege } from '../lib/protokoll';
import type { Eintrag } from '../lib/protokoll';

/** Mehr passt auf kein Handy, und weiter zurück hilft beim Absturz selten. */
const SICHTBARE_MELDUNGEN = 10;

type ErrorBoundaryProps = { children?: ReactNode };
type ErrorBoundaryState = { hasError: boolean; error: Error | null; verlauf: Eintrag[] };

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, verlauf: [] };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    protokoll.fehler('Absturz', 'Unbehandelter Fehler', error);
    protokoll.hinweis('Absturz', 'Aufrufkette', errorInfo?.componentStack);

    // Der Verlauf wandert in den Zustand, nicht direkt in die Ausgabe. Zwei
    // Gründe: React rendert den Ersatzbildschirm, *bevor* diese Methode läuft
    // – ein `eintraege()` im render() hätte den Absturz selbst nicht dabei.
    // Und so friert die Liste den Stand vom Absturz ein, statt sich bei jedem
    // weiteren Rendern zu verschieben.
    this.setState({ verlauf: eintraege().slice(0, SICHTBARE_MELDUNGEN) });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, verlauf: [] });
    // Seite neu laden als sichere Fallback-Option
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div className="error-boundary-container">
        <div className="error-boundary-card">
          <div className="error-boundary-icon">⚔️</div>
          <h2 className="error-boundary-title">Ein Fehler ist aufgetreten</h2>
          <p className="error-boundary-message">
            Ein unerwarteter Fehler hat diese Seite zum Absturz gebracht. Deine Daten sind sicher.
          </p>
          {this.state.error && (
            <details className="error-boundary-details">
              <summary>Technische Details</summary>
              <pre>{this.state.error.toString()}</pre>
              <ul className="error-boundary-protokoll" data-testid="protokoll-liste">
                {this.state.verlauf.map((eintrag) => (
                  <li key={`${eintrag.zeit}-${eintrag.meldung}`}>
                    <span className={`protokoll-stufe protokoll-${eintrag.stufe}`}>
                      {eintrag.stufe}
                    </span>{' '}
                    <strong>{eintrag.bereich}</strong> – {eintrag.meldung}
                    {eintrag.details ? `: ${eintrag.details}` : ''}
                  </li>
                ))}
              </ul>
            </details>
          )}
          <button className="button error-boundary-button" onClick={this.handleReset}>
            Seite neu laden
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
